<template>
  <div class="theme-selector container py-5">
    <!-- Header -->
    <div class="theme-header row justify-content-center mb-3">
      <div class="col-auto text-center">
        <h1 class="theme-title">Select Your Theme</h1>
        <p>Click theme icon to select</p>
      </div>
    </div>

    <!-- Pagination dots -->
    <div class="theme-dots row justify-content-center mb-4">
      <div class="col-auto">
        <div class="swiper-pagination"></div>
      </div>
    </div>

    <!-- Swiper with Custom Card Layout -->
    <div class="theme-swiper-row row justify-content-center">
      <div class="col-12">
        <div class="swiper-container main-swiper">
          <!-- Navigation arrows -->
          <div class="swiper-button-prev"></div>
          <div class="swiper-button-next"></div>
          <div class="swiper-wrapper">
            <!-- data-index is critical for the click handler -->
            <div v-for="(theme, index) in themes" :key="index" class="swiper-slide" :data-index="index">
              <div class="theme-card">
                <div class="card-inner">
                  <!-- Front of card -->
                  <div class="card-front">
                    <img :src="theme.image" :alt="theme.name">
                    <div class="theme-label" :class="getLabelClass(theme)">{{ theme.name.toUpperCase() }}</div>
                  </div>
                  
                  <!-- Back of card with description -->
                  <div class="card-back" :class="getBackgroundClass(theme)">
                    <div class="card-back-content">
                      <p>{{ getCustomDescription(theme) }}</p>
                    </div>
                    <div class="theme-label-back" :class="getLabelClass(theme)">{{ theme.name.toUpperCase() }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, getCurrentInstance } from 'vue';
import Swiper from 'swiper';
import { Pagination, EffectCoverflow, Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';

export default {
  name: 'ThemeSwiper',
  props: {
    profiles: {
      type: Array,
      required: true
    }
  },
  setup(props, { emit }) {
    const mainSwiper = ref(null);
    const instance = getCurrentInstance();
    
    const themes = [
      {
        name: 'Love',
        image: '/assets/images/theme selection/love.png',
        description: 'Expressing love for your pet',
        profileType: 'love'
      },
      {
        name: 'Gifting',
        image: '/assets/images/theme selection/gifting.png',
        description: 'For friends and family',
        profileType: 'gift'
      },
      {
        name: 'Memorial',
        image: '/assets/images/theme selection/memorial.png',
        description: 'For a pet that has passed away',
        profileType: 'memorialize'
      },
      {
        name: 'Wild',
        image: '/assets/images/theme selection/wild.png',
        description: 'Expressing love for wild animal or endangered',
        profileType: 'animal-love'
      },
      {
        name: 'Away',
        image: '/assets/images/theme selection/away.png',
        description: 'For when you\'re away from your pet',
        profileType: 'temporary-separation'
      }
    ];

    if (props.profiles && props.profiles.length > 0) {
      themes.forEach((theme, index) => {
        if (index < props.profiles.length && props.profiles[index]) {
          theme.profileId = props.profiles[index]._id;
        }
      });
    }

    const getLabelClass = (theme) => {
      const map = {
        'Love': 'love-label',
        'Gifting': 'gift-label',
        'Memorial': 'memorial-label',
        'Wild': 'wild-label',
        'Away': 'away-label'
      };
      return map[theme.name] || '';
    };

    const getBackgroundClass = (theme) => {
      const map = {
        'Love': 'love-bg',
        'Gifting': 'gift-bg',
        'Memorial': 'memorial-bg',
        'Wild': 'wild-bg',
        'Away': 'away-bg'
      };
      return map[theme.name] || '';
    };

    const getCustomDescription = (theme) => {
      return theme.description;
    };

    const selectTheme = (theme) => {
      if (!theme) return;
      
      // Check if Wild theme is selected
      if (theme.name === 'Wild' || theme.profileType === 'animal-love') {
        // Show warning message using SweetAlert2
        if (instance && instance.appContext.config.globalProperties.$showWarning) {
          instance.appContext.config.globalProperties.$showWarning(
            'This theme is under development, please check back later.',
            'Theme Under Development'
          );
        } else {
          // Fallback to alert if SweetAlert2 is not available
          alert('This theme is under development, please check back later.');
        }
        return; // Don't emit the event for Wild theme
      }
      
      emit('profileSelected', theme.profileType);
    };

    const handleSwiperClick = (swiper, event) => {
      const clickedSlide = swiper.clickedSlide;
      if (clickedSlide) {
        const index = clickedSlide.getAttribute('data-index');
        if (index !== null && themes[index]) {
          selectTheme(themes[index]);
        }
      } else {
        const target = event.target.closest('.theme-card');
        if (target) {
          const slide = target.closest('.swiper-slide');
          if (slide) {
            const index = slide.getAttribute('data-index');
            if (index !== null && themes[index]) {
              selectTheme(themes[index]);
            }
          }
        }
      }
    };

    // Extracted logic to ensure Z-Index is applied consistently across all events
    const updateZIndex = (swiper) => {
      const { slides } = swiper;
      for (let i = 0; i < slides.length; i += 1) {
        const slide = slides[i];
        const progress = slide.progress;
        const absProgress = Math.abs(progress);

        // Calculate z-index: closer to center = higher z-index
        let zIndex = 100;

        if (absProgress < 0.1) {
          zIndex = 1000; // Active slide significantly higher
        } else if (absProgress < 1.1) {
          // Neighbors (prev/next)
          // We add a tiny offset based on sign to slightly favor one side if needed, 
          // but mainly we want them just below active
          zIndex = 500; 
        } else {
          // Distant slides
          zIndex = Math.max(10, 500 - Math.floor(absProgress * 100));
        }

        slide.style.zIndex = zIndex;
        
        // Critical: Set opacity here to ensure sync with Z-index
        // Using Math.max to prevent negative values
        let opacity = 1;
        if (absProgress > 0.1) {
          opacity = Math.max(0.6, 1 - (absProgress * 0.3));
        }
        slide.style.opacity = opacity;
      }
    };

    onMounted(() => {
      mainSwiper.value = new Swiper('.main-swiper', {
        modules: [Pagination, EffectCoverflow, Autoplay, Navigation],
        effect: 'coverflow',
        grabCursor: true,
        centeredSlides: true,
        slidesPerView: 'auto',
        loop: true,
        spaceBetween: 0, 
        speed: 800,
        slideToClickedSlide: true, 
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        },
        coverflowEffect: {
          rotate: 0,
          stretch: -20, 
          depth: 200, 
          modifier: 1,
          slideShadows: false,
          scale: 0.85, 
        },
        watchSlidesProgress: true, // Essential for calculating progress
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        on: {
          click: handleSwiperClick,
          init: (swiper) => {
            // Force Z-Index calculation immediately on init
            updateZIndex(swiper);
            setTimeout(() => updateZIndex(swiper), 50); // Double check for async rendering
          },
          resize: (swiper) => {
             updateZIndex(swiper);
          },
          setTranslate: (swiper) => {
             updateZIndex(swiper);
          },
          setTransition: (swiper, duration) => {
            const { slides } = swiper;
            for (let i = 0; i < slides.length; i += 1) {
               slides[i].style.transitionDuration = `${duration}ms`;
            }
          },
        }
      });
    });

    return {
      themes,
      selectTheme,
      getLabelClass,
      getBackgroundClass,
      getCustomDescription
    };
  }
};
</script>

<style scoped>
.theme-header {
  text-align: center;
}

.theme-header .theme-title {
  margin: 0;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.2;
}

.theme-header p {
  margin: 0.5rem 0 0;
  color: #555;
}

.swiper-container {
  padding: 40px 0 60px;
  width: 90%;
  max-width: 800px;
  margin: 0 auto;
  overflow: visible; /* Changed from hidden to visible to prevent clipping 3D edges */
  position: relative;
}

.theme-selector {
  overflow: hidden; /* Keep the overflow hidden on the outer wrapper instead */
  position: relative;
}

/* 
   CRITICAL FIX: preserve-3d enables nested 3D transforms (flip inside swiper)
   pointer-events: none on wrapper prevents dead zones 
*/
:deep(.swiper-wrapper) {
  pointer-events: none; 
  transform-style: preserve-3d;
}

.theme-selector .row,
.theme-selector .row.justify-content-center {
  margin-left: 0;
  margin-right: 0;
}

/* 
   CRITICAL FIX: 
   1. pointer-events: none; -> The slide container itself shouldn't capture clicks.
      This allows clicks to pass through transparent overlapping areas of the slide container.
   2. transform-style: preserve-3d; -> Maintains the card flip 3D context.
*/
.swiper-slide {
  width: 280px; 
  transition: all 0.5s ease;
  transform-origin: center;
  pointer-events: none !important; 
  transform-style: preserve-3d;
}

/* Theme card styles */
.theme-card {
  width: 100%;
  height: 350px;
  cursor: pointer;
  perspective: 1000px; 
  /* CRITICAL FIX: Re-enable pointer events on the actual visible content */
  pointer-events: auto !important; 
  position: relative;
  z-index: inherit;
}

.card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}

/* Hover logic */
.swiper-slide:hover .theme-card .card-inner,
.theme-card:hover .card-inner {
  transform: rotateY(180deg);
}

.card-front, .card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden; /* Hides the back when facing away */
  /* -webkit-backface-visibility: hidden; for Safari compatibility if needed */
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1); /* Added shadow for depth */
}

.card-front {
  z-index: 2;
  background: white; /* Ensure front has a background */
}

.card-back {
  transform: rotateY(180deg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  color: white;
  font-size: 1.2rem;
  font-style: italic;
  z-index: 1; /* Ensure back is logically behind until flipped */
}

.card-back-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.card-back-content p {
  font-size: 1.8rem;
  line-height: 1.4;
  font-family: 'Georgia', serif;
  font-style: italic;
  margin: 0;
}

/* Background colors */
.love-bg { background-color: #e5a7a1; }
.gift-bg { background-color: #f19586; }
.memorial-bg { background-color: #c2dace; color: #333; }
.wild-bg { background-color: #dbc99c; }
.away-bg { background-color: #957548; }

.theme-card img {
  height: 320px;
  object-fit: contain;
}

/* Label styling */
.theme-label {
  display: none; /* Consider checking if you want this displayed */
  position: relative;
  padding: 6px 40px;
  border-radius: 40px;
  color: white;
  font-weight: bold;
  text-align: center;
  margin-top: -15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-width: 120px;
}

.theme-label-back {
  position: absolute;
  bottom: 15px;
  padding: 6px 40px;
  border-radius: 40px;
  color: white;
  font-weight: bold;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-width: 120px;
}

.love-label { background-color: #e5a7a1; }
.gift-label { background-color: #f19586; }
.memorial-label { background-color: #c2dace; color: #333; }
.wild-label { background-color: #bc9c22; }
.away-label { background-color: #957548; }

/* Pagination styling */
.theme-dots {
  display: flex;
  justify-content: center;
  overflow: visible !important;
}

:global(.swiper-pagination-bullet) {
  width: 12px;
  height: 12px;
  background: #EDE7E3;
  opacity: 1;
  margin: 0 5px;
}

:global(.swiper-pagination-bullet-active) {
  background: #EBA9AD;
}

.row{
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .swiper-container { max-width: 100%; width: 100%; }
  .theme-card { height: 300px; }
  .theme-card img { height: 250px; }
  .card-back-content p { font-size: 1.5rem; }
}

@media (max-width: 768px) {
  .swiper-container { padding: 10px 0 40px; width: 100%; }
  .card-back-content p { font-size: 1.2rem; }
}
.swiper-pagination{
  position: relative;
}

/* Navigation arrows styling */
.swiper-button-prev,
.swiper-button-next {
  color: #EBA9AD;
  width: 50px;
  height: 50px;
  margin-top: -25px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 10;
}

.swiper-button-prev:hover,
.swiper-button-next:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.swiper-button-prev:after,
.swiper-button-next:after {
  font-size: 20px;
  font-weight: bold;
}

.swiper-button-prev {
  left: 10px;
}

.swiper-button-next {
  right: 10px;
}

/* Responsive arrow adjustments */
@media (max-width: 768px) {
  .swiper-button-prev,
  .swiper-button-next {
    width: 40px;
    height: 40px;
    margin-top: -20px;
  }
  
  .swiper-button-prev:after,
  .swiper-button-next:after {
    font-size: 16px;
  }
  
  .swiper-button-prev {
    left: 5px;
  }
  
  .swiper-button-next {
    right: 5px;
  }
}
</style>